import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import api from '../api';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const RATING_OPTIONS = [
  { value: 'Excellent', label: 'Отлично' },
  { value: 'Good', label: 'Хорошо' },
  { value: 'Average', label: 'Нейтрально' },
  { value: 'Bad', label: 'Плохо' },
  { value: 'Awful', label: 'Ужасно' },
];

const RATING_LABELS = RATING_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const MOTIVATION_HELP_ITEMS = [
  'Отлично - глубоко вовлечен, берет ответственность за результат, самостоятельно предлагает улучшения.',
  'Хорошо - в целом заинтересован, задачи выполняет добросовестно, периодически проявляет инициативу.',
  'Нейтрально - делает, что поручено, без заметной инициативы или стремления выйти за рамки минимума.',
  'Плохо - часто пассивен, избегает дополнительной ответственности, мотивация выглядит низкой.',
  'Ужасно - явно демотивирован, затягивает задачи, может игнорировать договоренности и влияние на результат.',
];

const COMPETENCY_HELP_ITEMS = [
  'Отлично - уверенно решает сложные задачи, глубоко понимает предметную область, работает достаточно автономно.',
  'Хорошо - владеет нужными инструментами, в основном справляется самостоятельно, качество работы стабильно высокое.',
  'Нейтрально - с типовыми задачами справляется, но при усложнении заметно нужна поддержка, качество в целом приемлемое.',
  'Плохо - часто не хватает знаний или навыков, требуется частый контроль, результаты нестабильны.',
  'Ужасно - уровень явно не соответствует требованиям, систематически не справляется с задачами, мешает работе команды.',
];

const createEmptyRowDraft = () => ({
  motivationRating: '',
  competencyRating: '',
  text: '',
});

const buildInitialRowDrafts = (loadedEngineers) => loadedEngineers.reduce((acc, engineer) => {
  acc[String(engineer.id)] = createEmptyRowDraft();
  return acc;
}, {});

const parseSavedReport = (report) => {
  const text = String(report?.text || '');
  const motivation = text.match(/уровень мотивации специалиста[^:]*:\s*(.*)/i)?.[1]?.trim() || '';
  const competency = text.match(/уровень профессиональных компетенций специалиста[^:]*:\s*(.*)/i)?.[1]?.trim() || '';
  const feedbackMarker = 'Дополнительная обратная связь по сотруднику:';
  const feedbackIndex = text.indexOf(feedbackMarker);
  return {
    motivationRating: Object.keys(RATING_LABELS).find((key) => RATING_LABELS[key] === motivation) || motivation,
    competencyRating: Object.keys(RATING_LABELS).find((key) => RATING_LABELS[key] === competency) || competency,
    text: feedbackIndex >= 0 ? text.slice(feedbackIndex + feedbackMarker.length).trim() : '',
  };
};

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const formatDate = (value) => {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value).split('T')[0] : parsed.toLocaleDateString('ru-RU');
};

const SpreadsheetMetaRow = ({ label, value }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '280px minmax(0, 1fr)' },
      borderBottom: '1px solid #d8dee8',
      minHeight: 44,
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.25,
        fontWeight: 600,
        borderRight: { xs: 'none', sm: '1px solid #d8dee8' },
        backgroundColor: '#fbfcfe',
      }}
    >
      {label}
    </Box>
    <Box sx={{ px: 2, py: 1.25, color: '#344054', wordBreak: 'break-word' }}>
      {value || '—'}
    </Box>
  </Box>
);

const CustomerReportPage = () => {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const storageKey = useMemo(() => (token ? `customer-report-draft:${token}` : ''), [token]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectName, setProjectName] = useState('');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [engineers, setEngineers] = useState([]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [rowDrafts, setRowDrafts] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [ratingError, setRatingError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      if (!token) {
        setError('Ссылка не содержит токен.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/customer-links/public/${encodeURIComponent(token)}`);
        const payload = response?.data || {};
        const loadedEngineers = Array.isArray(payload.engineers) ? payload.engineers : [];
        setProjectName(payload.projectName || '');
        setReportingPeriod(payload.reportingPeriod || '');
        setExpiresAt(payload.expiresAt || '');
        setEngineers(loadedEngineers);
        let saved = {};
        try {
          saved = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
        } catch {
          saved = {};
        }
        const initialDrafts = buildInitialRowDrafts(loadedEngineers);
        loadedEngineers.forEach((engineer) => {
          const engineerId = String(engineer.id);
          initialDrafts[engineerId] = {
            ...initialDrafts[engineerId],
            ...(saved.rowDrafts?.[engineerId] || {}),
          };
        });
        (Array.isArray(payload.reports) ? payload.reports : []).forEach((report) => {
          const engineerId = String(report.engineerId);
          if (initialDrafts[engineerId]) initialDrafts[engineerId] = parseSavedReport(report);
        });
        const savedReportEmail = Array.isArray(payload.reports)
          ? payload.reports.find((report) => report.customerEmail)?.customerEmail
          : '';
        setCustomerEmail(savedReportEmail || saved.customerEmail || '');
        setRowDrafts(initialDrafts);
      } catch (requestError) {
        const status = requestError?.response?.status;
        if (status === 410) {
          setError('Срок действия ссылки истёк.');
        } else if (status === 404) {
          setError('Ссылка не найдена.');
        } else {
          setError(requestError?.response?.data?.message || 'Не удалось открыть форму заказчика.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [storageKey, token]);

  useEffect(() => {
    if (!storageKey || loading) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ customerEmail, rowDrafts }));
    } catch {
      // Сохранение в браузере недоступно — форма продолжает работать в текущей вкладке.
    }
  }, [customerEmail, loading, rowDrafts, storageKey]);

  const isEmailValid = EMAIL_PATTERN.test(normalizeEmail(customerEmail));

  const updateRowDraft = (engineerId, patch) => {
    setRowDrafts((prev) => ({
      ...prev,
      [engineerId]: {
        ...(prev[engineerId] || createEmptyRowDraft()),
        ...patch,
      },
    }));
  };

  const completedRows = engineers.filter((engineer) => {
    const draft = rowDrafts[String(engineer.id)] || createEmptyRowDraft();
    return Boolean(draft.motivationRating && draft.competencyRating);
  });

  const incompleteRows = engineers.filter((engineer) => {
    const draft = rowDrafts[String(engineer.id)] || createEmptyRowDraft();
    return !draft.motivationRating || !draft.competencyRating;
  });

  const handleSubmit = async () => {
    if (!token) return;
    if (!isEmailValid) {
      setSubmitError('Укажите корректный корпоративный email.');
      return;
    }
    if (incompleteRows.length > 0) {
      setRatingError('Пожалуйста, проставьте оценки мотивации и профессиональных компетенций для каждого сотрудника!');
      return;
    }
    if (completedRows.length === 0) {
      setSubmitError('Нет сотрудников для отправки отчёта.');
      return;
    }

    setSubmitError('');
    setSubmitSuccess('');
    setSubmitting(true);

    try {
      for (const engineer of completedRows) {
        const engineerId = String(engineer.id);
        const draft = rowDrafts[engineerId] || createEmptyRowDraft();
        const reportText = [
          `Оцените уровень мотивации специалиста (инициативность, вовлечённость, готовность брать ответственность): ${RATING_LABELS[draft.motivationRating] || draft.motivationRating}`,
          `Оцените уровень профессиональных компетенций специалиста (знание инструментов и технологий, понимание своих задач, качество и самостоятельность работы): ${RATING_LABELS[draft.competencyRating] || draft.competencyRating}`,
          '',
          'Дополнительная обратная связь по сотруднику:',
          draft.text.trim(),
        ].join('\n');

        await api.post(`/customer-links/public/${encodeURIComponent(token)}/reports`, {
          engineerId: Number(engineer.id),
          text: reportText,
          customerEmail: normalizeEmail(customerEmail),
        });
      }

      setSubmitSuccess(completedRows.length === 1 ? 'Отчёт отправлен.' : `Отчёты отправлены: ${completedRows.length}.`);
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status === 410) {
        setSubmitError('Срок действия ссылки истёк.');
      } else if (status === 403) {
        setSubmitError('Один из сотрудников недоступен по этой ссылке.');
      } else {
        setSubmitError(requestError?.response?.data?.message || 'Не удалось отправить отчёт.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 1.5, md: 3 },
        background: 'linear-gradient(180deg, #eef2f7 0%, #f8fafc 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          overflow: 'hidden',
          border: '1px solid #cfd7e3',
          borderRadius: 0,
          backgroundColor: '#ffffff',
        }}
      >
        <Stack spacing={0}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f3f4f6', borderBottom: '1px solid #cfd7e3' }}>
            <Typography sx={{ fontFamily: 'TT Travels, Roboto, sans-serif', fontSize: { xs: 22, md: 26 }, fontWeight: 700 }}>
              Обратная связь по сотрудникам
            </Typography>
          </Box>

          <SpreadsheetMetaRow label="Проект:" value={projectName} />
          <SpreadsheetMetaRow label="Отчет за период:" value={reportingPeriod} />
          <SpreadsheetMetaRow label="Отчет необходимо заполнить до:" value={formatDate(expiresAt)} />
          <Box sx={{ p: 2, backgroundColor: '#fff8e1', borderBottom: '2px solid #f2c94c' }}>
            <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Корпоративный email заказчика:</Typography>
            <TextField value={customerEmail} onChange={(event) => setCustomerEmail(event.target.value)} type="email" fullWidth placeholder="name@company.ru"
              error={Boolean(customerEmail) && !isEmailValid}
              helperText={Boolean(customerEmail) && !isEmailValid ? 'Введите корректный email' : 'Укажите email перед отправкой отчета'}
              sx={{ backgroundColor: '#fff', '& .MuiOutlinedInput-root': { border: '2px solid #e0a800' } }} />
          </Box>

          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {!error && (
            <>
              <Box sx={{ p: 2.5, backgroundColor: '#f8fafc', borderBottom: '1px solid #cfd7e3' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Пояснение и критерии оценки</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>Оцените мотивацию и профессиональные компетенции каждого сотрудника. Комментарий можно добавить при необходимости.</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box><Typography sx={{ fontWeight: 700, mb: 0.5 }}>Мотивация</Typography>{MOTIVATION_HELP_ITEMS.map((item) => <Typography key={item} variant="body2" sx={{ mb: 0.35 }}>• {item}</Typography>)}</Box>
                  <Box><Typography sx={{ fontWeight: 700, mb: 0.5 }}>Профессиональные компетенции</Typography>{COMPETENCY_HELP_ITEMS.map((item) => <Typography key={item} variant="body2" sx={{ mb: 0.35 }}>• {item}</Typography>)}</Box>
                </Box>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 960 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ border: '1px solid #cfd7e3', fontWeight: 700, width: 260 }}>ФИО</TableCell>
                      <TableCell sx={{ border: '1px solid #cfd7e3', fontWeight: 700, width: 220 }}>Команды</TableCell>
                      <TableCell sx={{ border: '1px solid #cfd7e3', fontWeight: 700, width: 220 }}>
                        Оцените уровень мотивации
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #cfd7e3', fontWeight: 700, width: 250 }}>
                        Оцените уровень проф компетенций
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #cfd7e3', fontWeight: 700, minWidth: 320 }}>Комментарии (при необходимости)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engineers.map((engineer) => {
                      const engineerId = String(engineer.id);
                      const draft = rowDrafts[engineerId] || createEmptyRowDraft();
                      const teams = Array.isArray(engineer.teamNames) ? engineer.teamNames.join(', ') : '—';
                      const fullName = `${engineer.surname || ''} ${engineer.name || ''}`.trim() || '—';
                      const rowHasValue = Boolean(draft.motivationRating || draft.competencyRating || draft.text.trim());

                      return (
                        <TableRow
                          key={engineer.id}
                          sx={{
                            backgroundColor: rowHasValue ? '#fffdf7' : '#ffffff',
                            '& td': { verticalAlign: 'top' },
                          }}
                        >
                          <TableCell sx={{ border: '1px solid #d8dee8' }}>
                            <Typography sx={{ fontWeight: 600 }}>{fullName}</Typography>
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #d8dee8', color: '#475467' }}>{teams || '—'}</TableCell>
                          <TableCell sx={{ border: '1px solid #d8dee8' }}>
                            <Select
                              value={draft.motivationRating}
                              onChange={(event) => updateRowDraft(engineerId, { motivationRating: event.target.value })}
                              displayEmpty
                              disabled={submitting}
                              fullWidth
                              size="small"
                              renderValue={(value) => (value ? (RATING_LABELS[value] || value) : 'Выберите')}
                            >
                              <MenuItem value="">Выберите</MenuItem>
                              {RATING_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #d8dee8' }}>
                            <Select
                              value={draft.competencyRating}
                              onChange={(event) => updateRowDraft(engineerId, { competencyRating: event.target.value })}
                              displayEmpty
                              disabled={submitting}
                              fullWidth
                              size="small"
                              renderValue={(value) => (value ? (RATING_LABELS[value] || value) : 'Выберите')}
                            >
                              <MenuItem value="">Выберите</MenuItem>
                              {RATING_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #d8dee8' }}>
                            <TextField
                              value={draft.text}
                              onChange={(event) => updateRowDraft(engineerId, { text: event.target.value })}
                              multiline
                              minRows={3}
                              fullWidth
                              placeholder="Комментарий по сотруднику"
                              disabled={submitting}
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {engineers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ border: '1px solid #d8dee8', textAlign: 'center', py: 3 }}>
                          Доступные инженеры не найдены.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ p: 2.5 }}>
                {!isEmailValid && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Сначала укажите корректный корпоративный email, затем заполните строки таблицы.
                  </Typography>
                )}
                {submitError && <Alert severity="error" sx={{ mb: 1.5 }}>{submitError}</Alert>}
                {submitSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{submitSuccess}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'center' } }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{
                      minWidth: 240,
                      minHeight: 48,
                      borderRadius: 1,
                      fontWeight: 700,
                      boxShadow: 'none',
                    }}
                  >
                    {submitting ? 'Отправка...' : 'Отправить отчет'}
                  </Button>
                </Box>
              </Box>
              <Snackbar
                open={Boolean(ratingError)}
                autoHideDuration={6000}
                onClose={() => setRatingError('')}
                anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
              >
                <Alert severity="warning" onClose={() => setRatingError('')} sx={{ fontWeight: 700, boxShadow: 6 }}>
                  {ratingError}
                </Alert>
              </Snackbar>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default CustomerReportPage;
